import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AttentionHero,
  AttentionRow,
  Box,
  EmptyState,
  Icon,
  Screen,
  Text,
} from '~/atomic';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectAlertHero,
  selectContractsList,
  selectNextInstallmentByContract,
  selectOverdueAttention,
  type AttentionItem,
} from '~/store/contracts/selectors';
import { fetchContracts } from '~/store/contracts/thunks';

const MAX_OVERDUE_VISIBLE = 3;

export default function AlertsView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const contracts = useAppSelector(selectContractsList);
  const hero = useAppSelector(selectAlertHero);
  const overdues = useAppSelector(selectOverdueAttention);
  const nextPerContract = useAppSelector(selectNextInstallmentByContract);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (contracts.length === 0) dispatch(fetchContracts());
  }, [dispatch, contracts.length]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchContracts());
    setRefreshing(false);
  };

  const hasContracts = contracts.length > 0;

  const headerSubtitle = (() => {
    if (hero.overdueCount > 0) {
      return `${hero.overdueCount} ${
        hero.overdueCount === 1 ? 'parcela atrasada' : 'parcelas atrasadas'
      }`;
    }
    if (hero.upcoming30Count > 0) {
      return `${hero.upcoming30Count} ${
        hero.upcoming30Count === 1 ? 'parcela em 30 dias' : 'parcelas em 30 dias'
      }`;
    }
    return 'Nenhuma parcela em aberto';
  })();

  const openInstallment = (item: AttentionItem) =>
    navigate(
      `/app/contracts/${item.installment.contractId}/installments/${item.installment.id}`,
    );

  const handleCta = () => {
    if (!hero.focus) return;
    openInstallment(hero.focus);
  };

  const overdueVisible = overdues.slice(0, MAX_OVERDUE_VISIBLE);
  const hiddenOverdueCount = Math.max(0, overdues.length - MAX_OVERDUE_VISIBLE);
  const nextItems = nextPerContract.slice(0, 8);

  return (
    <Screen
      showHeader={false}
      hasTabNavigation
      hasPadding={false}
      refreshLoading={refreshing}
      handleRefresh={handleRefresh}
    >
      <div className="px-small pt-medium pb-large">
        <div className="flex items-center justify-between pt-small">
          <div className="flex-1 mr-small">
            <Text className="text-headingH1 color-complement-01" as="h1">
              Alertas
            </Text>
            <Text className="mt-quark text-bodyMD color-intense-06">
              {headerSubtitle}
            </Text>
          </div>
          <button
            type="button"
            onClick={() => navigate('/app/notifications')}
            className="w-[44px] h-[44px] rounded-[14px] bg-intense-03 flex items-center justify-center hover:opacity-90"
            aria-label="Notificações"
          >
            <Icon name="icon-notification" size={20} color="#606060" rounded={false} />
          </button>
        </div>

        {hasContracts ? (
          <>
            <AttentionHero summary={hero} onCta={handleCta} className="mt-medium" />

            {overdues.length > 0 ? (
              <Box className="mt-large flex flex-col gap-smaller">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-quark">
                    <span className="h-[8px] w-[8px] rounded-full bg-warning-04" />
                    <Text className="text-paragraphMD color-complement-01">
                      Pagamentos atrasados
                    </Text>
                  </div>
                  <span className="inline-flex items-center px-smaller py-quark rounded-full bg-warning-01">
                    <Text className="text-captionLG color-warning-06">
                      {overdues.length}
                    </Text>
                  </span>
                </div>

                <div className="flex flex-col gap-smaller">
                  {overdueVisible.map((item) => (
                    <AttentionRow
                      key={item.installment.id}
                      item={item}
                      onClick={() => openInstallment(item)}
                    />
                  ))}
                </div>

                {hiddenOverdueCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate('/app/alerts/all')}
                    className="mt-quark flex items-center justify-center gap-quark py-smaller rounded-small hover:bg-intense-02"
                  >
                    <Text className="text-paragraphMD color-warning-06">
                      Ver todas as {overdues.length} pendências
                    </Text>
                    <Icon
                      name="icon-arrow-right"
                      size={16}
                      color="#992929"
                      rounded={false}
                    />
                  </button>
                ) : null}
              </Box>
            ) : null}

            {nextItems.length > 0 ? (
              <Box className="mt-large flex flex-col gap-smaller">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-quark">
                    <span className="h-[8px] w-[8px] rounded-full bg-primary-01" />
                    <Text className="text-paragraphMD color-complement-01">
                      Próxima parcela por contrato
                    </Text>
                  </div>
                  <Text className="text-captionLG color-intense-06">
                    {nextItems.length} {nextItems.length === 1 ? 'contrato' : 'contratos'}
                  </Text>
                </div>

                <div className="flex flex-col gap-smaller">
                  {nextItems.map((item) => (
                    <AttentionRow
                      key={item.installment.id}
                      item={item}
                      onClick={() => openInstallment(item)}
                    />
                  ))}
                </div>

                {hero.upcoming30Count + hero.overdueCount > nextItems.length ? (
                  <button
                    type="button"
                    onClick={() => navigate('/app/alerts/all')}
                    className="mt-quark flex items-center justify-center gap-quark py-smaller rounded-small hover:bg-intense-02"
                  >
                    <Text className="text-paragraphMD color-primary-01">
                      Ver tudo que precisa de atenção
                    </Text>
                    <Icon
                      name="icon-arrow-right"
                      size={16}
                      color="#1D4733"
                      rounded={false}
                    />
                  </button>
                ) : null}
              </Box>
            ) : null}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center mt-large">
            <EmptyState
              icon="icon-document"
              title="Nenhum contrato ainda"
              description="Cadastre seu primeiro contrato para acompanhar vencimentos e receber alertas por aqui."
              ctaText="Adicionar contrato"
              onClickCta={() => navigate('/app/add')}
            />
          </div>
        )}
      </div>
    </Screen>
  );
}
